// src/components/managed-sources/ReviewStep.tsx

import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Slider,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconCalendar,
  IconCategory,
  IconClock,
  IconHistory,
  IconLink,
  IconRocket,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import { ConnectorIcon } from "@/components/data-sources/ConnectorIcon";
import { DocsLink } from "@/components/docs/DocsLink";
import { LaunchProgress } from "@/components/managed-sources/LaunchProgress";
import { DataArrivalModeField } from "@/components/managed-sources/DataArrivalSettings";
import type {
  FreshnessMode,
  ManagedSource,
  ManagedSourceCategory,
  ScheduleInterval,
} from "@/types/managedSources";

type Props = {
  sourceName: string;
  onSourceNameChange: (value: string) => void;
  connectorLabel: string;
  connectorType: string;
  authorizationName: string | null;
  accountLabel: string;
  accountLabelTitle: string;
  categories: ManagedSourceCategory[];
  selectedCategories: string[];
  scheduleInterval: ScheduleInterval;
  onScheduleChange: (value: ScheduleInterval) => void;
  scheduleHour: number;
  onScheduleHourChange: (hour: number) => void;
  scheduleCron: string;
  onScheduleCronChange: (cron: string) => void;
  backfillDays: number;
  onBackfillDaysChange: (days: number) => void;
  /** Resolved answer to "when does new data arrive in this source?" — the
   *  customer's choice if they made one, else the connector's own default.
   *  Rendered only for bring-your-own-data creates. */
  freshnessMode: FreshnessMode;
  onFreshnessModeChange: (value: FreshnessMode) => void;
  includeInOverview: boolean;
  onIncludeInOverviewChange: (value: boolean) => void;
  verityIdWriteback: boolean;
  onVerityIdWritebackChange: (value: boolean) => void;
  /** True when the connector can back segments directly — the switch below is
   *  rendered only then. */
  segmentationSupported?: boolean;
  /** Data-category keys that must ALL be selected first (e.g. ["customers",
   *  "orders"]), or null/empty when segmentation has no prerequisite. */
  segmentationRequiredCategories?: string[] | null;
  useAsSegmentationSource: boolean;
  onUseAsSegmentationSourceChange: (value: boolean) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
  success: boolean;
  createdSource?: ManagedSource | null;
  onClose: () => void;
  isTransformOnly?: boolean;
  /** False for a connector whose registry default is no backfill (the Verity
   *  Pixel: its events only exist from install on). Hides the backfill summary
   *  row and slider; the wizard then sends no `backfill_days`. Default true. */
  hasBackfill?: boolean;
  /** GA4 BigQuery Export only: the zone the source will be stored with, for the
   *  summary row. The wizard sends no `schedule_timezone`, so leave it unset
   *  and the platform default below is what the customer sees — the same value
   *  the backend falls back to. */
  propertyTimezone?: string | null;
};

/** What the customer reads when the wizard passes no zone. The backend then
 *  stores `payload.schedule_timezone` → the connector account's own zone
 *  (Google Ads only) → the Verity account's zone → `Europe/Amsterdam`
 *  (`setup_routes.py::create_managed_source`). The wizard cannot see the
 *  account's zone, so it names the rule rather than guessing a literal that
 *  would be wrong for every account that has one. */
export const DEFAULT_PROPERTY_TIMEZONE_LABEL = "Your account's time zone";

const SCHEDULE_LABELS: Record<ScheduleInterval, string> = {
  HOURLY: "Hourly",
  TWICE_DAILY: "Twice a day",
  DAILY: "Daily",
  CUSTOM: "Custom",
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${String(i).padStart(2, "0")}:00`,
}));

const CRON_PRESETS = [
  { label: "Weekdays at 6am", value: "0 6 * * 1-5" },
  { label: "Weekdays 6am + 6pm", value: "0 6,18 * * 1-5" },
  { label: "Every 4 hours", value: "0 */4 * * *" },
];

/* ConnectorIcon component handles connector type → icon mapping */

const BACKFILL_MARKS: Record<string, { value: number; label: string }[]> = {
  default: [
    { value: 30, label: "1 mo" },
    { value: 90, label: "3 mo" },
    { value: 180, label: "6 mo" },
    { value: 395, label: "13 mo" },
    { value: 730, label: "24 mo" },
  ],
  GOOGLE_ADS: [
    { value: 30, label: "1 mo" },
    { value: 180, label: "6 mo" },
    { value: 395, label: "13 mo" },
    { value: 730, label: "24 mo" },
    { value: 1095, label: "36 mo" },
  ],
  // Microsoft Advertising keeps 36 months of history, same as Google Ads.
  MICROSOFT_ADS: [
    { value: 30, label: "1 mo" },
    { value: 180, label: "6 mo" },
    { value: 395, label: "13 mo" },
    { value: 730, label: "24 mo" },
    { value: 1095, label: "36 mo" },
  ],
  // Search Console keeps ~16 months, so there is no 24-month tick to offer.
  GOOGLE_SEARCH_CONSOLE: [
    { value: 30, label: "1 mo" },
    { value: 90, label: "3 mo" },
    { value: 180, label: "6 mo" },
    { value: 395, label: "13 mo" },
    { value: 480, label: "16 mo" },
  ],
};

/**
 * Longest backfill this connector's SOURCE can actually answer, in days.
 *
 * Not a warehouse limit — a limit of the API on the other end. The backend
 * accepts anything from 30 to 1095 for every connector, so a slider that offers
 * more than the source retains does not fail: it silently produces a run whose
 * extra days come back empty, and a customer who asked for two years of Search
 * Console data would have no way to tell that from a broken pipeline.
 *
 * Default (absent from this map) is 24 months.
 */
const BACKFILL_MAX_DAYS: Record<string, number> = {
  GOOGLE_ADS: 1095,
  MICROSOFT_ADS: 1095,
  // Search Console's history horizon is 16 months and Google returns empty
  // days beyond it. 480 days is just inside that.
  GOOGLE_SEARCH_CONSOLE: 480,
};
const DEFAULT_BACKFILL_MAX_DAYS = 730;

export function backfillMaxDays(connectorType: string): number {
  return BACKFILL_MAX_DAYS[connectorType] ?? DEFAULT_BACKFILL_MAX_DAYS;
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <Box c="dimmed" style={{ display: "flex" }}>
          {icon}
        </Box>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Text size="sm" fw={500} ta="right">
        {value}
      </Text>
    </Group>
  );
}

export default function ReviewStep({
  sourceName,
  onSourceNameChange,
  connectorLabel,
  connectorType,
  authorizationName,
  accountLabel,
  accountLabelTitle,
  categories,
  selectedCategories,
  scheduleInterval,
  onScheduleChange,
  scheduleHour,
  onScheduleHourChange,
  scheduleCron,
  onScheduleCronChange,
  backfillDays,
  onBackfillDaysChange,
  freshnessMode,
  onFreshnessModeChange,
  includeInOverview,
  onIncludeInOverviewChange,
  verityIdWriteback,
  onVerityIdWritebackChange,
  segmentationSupported,
  segmentationRequiredCategories,
  useAsSegmentationSource,
  onUseAsSegmentationSourceChange,
  onSubmit,
  submitting,
  error,
  success,
  createdSource,
  onClose,
  isTransformOnly,
  hasBackfill = true,
  propertyTimezone,
}: Props) {
  const selectedCategoryLabels = categories
    .filter((c) => selectedCategories.includes(c.key))
    .map((c) => c.label);

  // Segmentation needs its source categories (Brevo: contacts; Shopify:
  // customers + orders) — without every one of them there are no complete
  // customer profiles to segment on. Show the switch disabled and name the
  // missing categories rather than hiding it: the customer has to be able to
  // see the option exists and what it costs to unlock.
  const segmentationMissingCategories = (
    segmentationRequiredCategories ?? []
  ).filter((key) => !selectedCategories.includes(key));
  const segmentationCategoryMissing = segmentationMissingCategories.length > 0;
  const segmentationCategoryLabel = segmentationMissingCategories
    .map((key) => categories.find((c) => c.key === key)?.label ?? key)
    .join("” and “");

  const backfillLabel =
    backfillDays <= 31
      ? "1 month"
      : `~${Math.round(backfillDays / 30)} months`;

  // --- Success state ---
  // Rendered only once we hold the created source: LaunchProgress follows the
  // real pipeline, so a boolean alone is not enough to identify what to follow.
  if (success && createdSource) {
    return (
      <LaunchProgress
        createdSource={createdSource}
        connectorLabel={connectorLabel}
        isTransformOnly={!!isTransformOnly}
        backfillLabel={backfillLabel}
        scheduleLabel={SCHEDULE_LABELS[scheduleInterval].toLowerCase()}
        onClose={onClose}
      />
    );
  }

  // --- Review state ---
  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Text fw={700} size="lg">
          Review & launch
        </Text>
        <Text size="sm" c="dimmed">
          Confirm your pipeline configuration.
        </Text>
      </Stack>

      <TextInput
        label="Source name"
        description="A recognizable name for this data source"
        placeholder={`${connectorLabel} (Managed)`}
        value={sourceName}
        onChange={(e) => onSourceNameChange(e.currentTarget.value)}
        maxLength={200}
        leftSection={<ConnectorIcon type={connectorType} size={18} />}
      />

      <Switch
        checked={includeInOverview}
        onChange={(e) => onIncludeInOverviewChange(e.currentTarget.checked)}
        label="Include in account overview"
        description="Combine this source's totals into account-wide dashboards (e.g. Marketing Overview). You can change this later."
      />

      {/* Segmentation opt-in — a primary capability choice, so it lives here
          and not behind Advanced settings. Rendered only for connectors that
          can back segments (the connector declares it), default OFF. */}
      {segmentationSupported && (
        <Switch
          checked={useAsSegmentationSource && !segmentationCategoryMissing}
          disabled={segmentationCategoryMissing}
          onChange={(e) =>
            onUseAsSegmentationSourceChange(e.currentTarget.checked)
          }
          label="Use as segmentation source"
          description={
            <>
              Make the people in this source available for building segments,
              after the first data refresh completes.
              {segmentationCategoryMissing && (
                <Text component="span" display="block" size="xs" c="dimmed" mt={4}>
                  Requires the &ldquo;{segmentationCategoryLabel}&rdquo; data{" "}
                  {segmentationMissingCategories.length > 1
                    ? "categories"
                    : "category"}
                  .
                </Text>
              )}
            </>
          }
        />
      )}

      <Paper withBorder p="lg" radius="md">
        <Stack gap="sm">
          {/* Connector */}
          <Group gap="xs">
            {<ConnectorIcon type={connectorType} size={18} />}
            <Text fw={600} size="sm">
              {connectorLabel}
            </Text>
          </Group>

          <Divider />

          {/* Account / Property */}
          <SummaryRow
            icon={<IconLink size={16} />}
            label={accountLabelTitle}
            value={
              <Text span ff="monospace" size="sm">
                {accountLabel}
              </Text>
            }
          />

          {/* Categories */}
          <SummaryRow
            icon={<IconCategory size={16} />}
            label="Data categories"
            value={selectedCategoryLabels.join(", ")}
          />

          {/* Schedule — sources Verity refreshes itself only. On a
              bring-your-own-data source the schedule steers nothing: every
              reader of it on the account-level transform path is bound to an
              ingest landing or to DAGSTER_INGEST (verified, plan 2026-09-13
              intraday v2, step 8b). "Data arrival" below is the real answer. */}
          {!isTransformOnly && (
            <SummaryRow
              icon={<IconCalendar size={16} />}
              label="Sync schedule"
              value={SCHEDULE_LABELS[scheduleInterval]}
            />
          )}

          {/* Data arrival — the one thing about a bring-your-own-data source
              only the customer knows. Summarised here so the pre-selected
              answer is visible without opening Advanced settings, where it can
              be changed (plan 2026-08-08, Step 10). */}
          {isTransformOnly && (
            <SummaryRow
              icon={<IconClock size={16} />}
              label="Data arrival"
              value={
                freshnessMode === "FRESHNESS"
                  ? "Timing varies"
                  : "On a schedule I control"
              }
            />
          )}

          {/* Property time zone — GA4 BigQuery Export only. Google names every
              export shard after the date in the PROPERTY's zone, so this is what
              decides which day a visit is counted in (plan 2026-09-13 intraday
              v2, Beslissing 10). The wizard does not ask for it, so what is
              summarised here is what the platform will store. */}
          {connectorType === "GA4_BQ_EXPORT" && (
            <SummaryRow
              icon={<IconClock size={16} />}
              label="Property time zone"
              value={
                <Text span size="sm">
                  {propertyTimezone ?? DEFAULT_PROPERTY_TIMEZONE_LABEL}
                  <Text span size="xs" c="dimmed">
                    {" "}
                    — change it in the source&apos;s settings if your property
                    reports in another zone
                  </Text>
                </Text>
              }
            />
          )}

          {/* Backfill */}
          {!isTransformOnly && hasBackfill && (
            <SummaryRow
              icon={<IconHistory size={16} />}
              label="Historical backfill"
              value={backfillLabel}
            />
          )}
        </Stack>
      </Paper>

      {/* Advanced settings accordion */}
      <Accordion variant="contained" radius="md">
        <Accordion.Item value="settings">
          <Accordion.Control icon={<IconSettings size={16} />}>
            <Text size="sm" fw={500}>Advanced settings</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              {/* Bring-your-own-data only: on a source Verity refreshes itself
                  the arrival is ours, so there is no answer the customer could
                  give — the backend refuses the field there. Sits directly
                  above the schedule because it changes what that schedule
                  means. */}
              {isTransformOnly && (
                <>
                  <DataArrivalModeField
                    value={freshnessMode}
                    onChange={onFreshnessModeChange}
                  />
                  <Divider />
                </>
              )}

              {/* Every schedule control below is for sources Verity refreshes
                  itself. On a bring-your-own-data source they drive nothing
                  (see the summary row above), and the wizard keeps sending its
                  default so the create payload is unchanged. */}
              {!isTransformOnly && (
              <>
              <Select
                label="Sync schedule"
                description="How often your data is refreshed"
                data={[
                  { value: "DAILY", label: "Daily (Recommended)" },
                  { value: "TWICE_DAILY", label: "Twice a day" },
                  { value: "HOURLY", label: "Hourly" },
                  { value: "CUSTOM", label: "Custom (cron)" },
                ]}
                value={scheduleInterval}
                onChange={(v) => {
                  if (v) onScheduleChange(v as ScheduleInterval);
                }}
                size="sm"
                w={280}
              />

              {/* Time picker for DAILY */}
              {scheduleInterval === "DAILY" && (
                <Select
                  label="Preferred time"
                  description="Hour of day when the sync should run"
                  data={HOUR_OPTIONS}
                  value={String(scheduleHour)}
                  onChange={(v) => {
                    if (v) onScheduleHourChange(Number(v));
                  }}
                  size="sm"
                  w={160}
                />
              )}

              {/* Two time pickers for TWICE_DAILY */}
              {scheduleInterval === "TWICE_DAILY" && (() => {
                const cronMatch = scheduleCron?.match(/^0 (\d+),(\d+) \* \* \*$/);
                const hour1 = cronMatch ? Number(cronMatch[1]) : scheduleHour;
                const hour2 = cronMatch ? Number(cronMatch[2]) : (scheduleHour + 12) % 24;
                return (
                  <Group gap="sm">
                    <Select
                      label="First run"
                      data={HOUR_OPTIONS}
                      value={String(hour1)}
                      onChange={(v) => {
                        if (!v) return;
                        const h1 = Number(v);
                        const sorted = [h1, hour2].sort((a, b) => a - b);
                        onScheduleHourChange(h1);
                        onScheduleCronChange(`0 ${sorted.join(",")} * * *`);
                      }}
                      size="sm"
                      w={130}
                    />
                    <Select
                      label="Second run"
                      data={HOUR_OPTIONS}
                      value={String(hour2)}
                      onChange={(v) => {
                        if (!v) return;
                        const h2 = Number(v);
                        const sorted = [hour1, h2].sort((a, b) => a - b);
                        onScheduleCronChange(`0 ${sorted.join(",")} * * *`);
                      }}
                      size="sm"
                      w={130}
                    />
                  </Group>
                );
              })()}

              {/* Cron input (only for CUSTOM) */}
              {scheduleInterval === "CUSTOM" && (
                <Stack gap="xs">
                  <TextInput
                    label="Cron expression"
                    description="5-field cron (minute hour day month weekday)"
                    placeholder="0 6,18 * * 1-5"
                    value={scheduleCron}
                    onChange={(e) => onScheduleCronChange(e.currentTarget.value)}
                    size="sm"
                    w={280}
                  />
                  <Group gap="xs">
                    {CRON_PRESETS.map((p) => (
                      <Badge
                        key={p.value}
                        variant="light"
                        color="gray"
                        size="sm"
                        style={{ cursor: "pointer", textTransform: "none" }}
                        onClick={() => onScheduleCronChange(p.value)}
                      >
                        {p.label}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              )}
              </>
              )}

              {!isTransformOnly && hasBackfill && (
                <Box>
                  <Text size="sm" fw={500} mb={4}>
                    Historical backfill
                  </Text>
                  <Text size="xs" c="dimmed" mb="sm">
                    13 months recommended for year-over-year comparisons
                  </Text>
                  <Slider
                    value={backfillDays}
                    onChange={onBackfillDaysChange}
                    min={30}
                    max={backfillMaxDays(connectorType)}
                    step={1}
                    marks={BACKFILL_MARKS[connectorType] || BACKFILL_MARKS.default}
                    label={(v) => {
                      if (v <= 31) return "1 month";
                      return `${Math.round(v / 30)} months`;
                    }}
                    styles={{ markLabel: { fontSize: 10 } }}
                  />
                  <Text size="xs" c="dimmed" ta="center" mt="md">
                    {backfillDays <= 31
                      ? "1 month of historical data"
                      : `~${Math.round(backfillDays / 30)} months of historical data`}
                    {backfillDays >= 365 && " — enables year-over-year comparison"}
                  </Text>
                </Box>
              )}

              {/* Brevo-only: Verity-ID write-back opt-in (default OFF). Mirrors
                  the toggle in the source detail drawer; only shown when the
                  contacts category is selected, since the write-back runs as
                  part of the contacts sync. */}
              {connectorType === "BREVO" && selectedCategories.includes("contacts") && (
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500}>Write Verity ID to contacts</Text>
                    <Text size="xs" c="dimmed">
                      Adds a <b>VERITY_ID</b> attribute to every Brevo contact
                      containing a Verity identifier derived from their email.
                      Reference it in email links to recognise returning website
                      visitors. Opt-in — this writes to your Brevo account.{" "}
                      <DocsLink slug="data-management/sources/connecting/brevo-verity-id">
                        Learn more
                      </DocsLink>
                    </Text>
                  </div>
                  <Switch
                    checked={verityIdWriteback}
                    onChange={(e) => onVerityIdWritebackChange(e.currentTarget.checked)}
                    size="sm"
                  />
                </Group>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {error && (
        <Alert color="red" icon={<IconX size={16} />}>
          {error}
        </Alert>
      )}

      <Button
        onClick={onSubmit}
        loading={submitting}
        size="md"
        fullWidth
        leftSection={<IconRocket size={18} />}
      >
        Launch pipeline
      </Button>

      <Text size="xs" c="dimmed" ta="center">
        {isTransformOnly
          ? "This will create a data source and configure the transform pipeline."
          : hasBackfill
            ? "This will create a data source, configure the pipeline, and start the initial data backfill."
            : "This will create a data source and configure the pipeline."}
      </Text>
    </Stack>
  );
}
